import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import BookingStep1 from '../components/booking/BookingStep1';
import BookingStep2 from '../components/booking/BookingStep2';
import BookingStep3 from '../components/booking/BookingStep3';
import logo from '../assets/Logo.webp';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

const STEPS = ['Event Details', 'Booth & Add-ons', 'Review & Price'];

export default function BookingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [pricing, setPricing] = useState(null);
  const [booths, setBooths] = useState([]);
  const [backdrops, setBackdrops] = useState([]);
  const [dbAddons, setDbAddons] = useState([]);
  const [form, setForm] = useState({
    // Step 1
    full_name: '', email: '', phone_number: '',
    event_date: '', start_time: '', duration_hours: 2,
    strip_wordings: '', venue_address: '', event_theme_description: '',
    // Step 2
    booth_type: '', photo_strip_style: '',
    selected_addons: [], // array of { id, quantity }
    backdrop_id: null, backdrop_name: '', backdrop_price: 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/pricing`).then(r => setPricing(r.data.pricing)).catch(() => {});
    axios.get(`${API}/api/booths`).then(r => setBooths(r.data.booths)).catch(() => {});
    axios.get(`${API}/api/backdrops`).then(r => setBackdrops(r.data.backdrops || [])).catch(() => {});
    axios.get(`${API}/api/addons`).then(r => setDbAddons(r.data.addons || [])).catch(() => {});
  }, []);

  const update = (fields) => setForm(prev => ({ ...prev, ...fields }));

  const handleNext = (stepErrors) => {
    if (Object.keys(stepErrors || {}).length) { setErrors(stepErrors); return; }
    setErrors({});
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  // ─── Live price calculation ─────────────────────────────────────────────────
  const calcSubtotal = () => {
    let total = 0;

    // Booth base rate
    const booth = booths.find(b => b.title === form.booth_type);
    if (booth) {
      total += parseFloat(booth.base_price) || 0;
      const duration = parseInt(form.duration_hours) || 2;
      if (duration > 2) {
        const extraHrs = duration - 2;
        const hourlyAddon = dbAddons.find(a => a.type === 'hourly');
        const rate = hourlyAddon ? parseFloat(hourlyAddon.price) : 75.00;
        total += extraHrs * rate;
      }
    }

    // Add-ons (dynamic database-driven)
    if (form.selected_addons && Array.isArray(form.selected_addons)) {
      form.selected_addons.forEach(item => {
        const addon = dbAddons.find(a => a.id === item.id);
        if (addon) {
          const qty = parseInt(item.quantity) || 1;
          if (addon.type === 'boolean') {
            total += parseFloat(addon.price) || 0;
          } else {
            total += (parseFloat(addon.price) || 0) * qty;
          }
        }
      });
    }

    // Backdrop addon price
    if (form.backdrop_price) total += parseFloat(form.backdrop_price) || 0;

    return isNaN(total) ? 0 : parseFloat(total.toFixed(2));
  };

  const calcTax = (subtotal) => {
    return parseFloat((subtotal * 0.0825).toFixed(2));
  };

  const calcTotal = () => {
    const sub = calcSubtotal();
    const tax = calcTax(sub);
    return parseFloat((sub + tax).toFixed(2));
  };

  const handleSubmit = async () => {
    const booth = booths.find(b => b.title === form.booth_type);
    let boothBaseRate = 0;
    if (booth) {
      boothBaseRate = parseFloat(booth.base_price) || 0;
      const duration = parseInt(form.duration_hours) || 2;
      if (duration > 2) {
        const extraHrs = duration - 2;
        const hourlyAddon = dbAddons.find(a => a.type === 'hourly');
        const rate = hourlyAddon ? parseFloat(hourlyAddon.price) : 75.00;
        boothBaseRate += extraHrs * rate;
      }
    }
    const subtotal = calcSubtotal();
    const tax = calcTax(subtotal);
    const total = parseFloat((subtotal + tax).toFixed(2));

    const bookingData = {
      full_name: form.full_name,
      email: form.email,
      event_date: form.event_date,
      start_time: form.start_time,
      duration_hours: parseInt(form.duration_hours) || 2,
      strip_wordings: form.strip_wordings || null,
      venue_address: form.venue_address,
      phone_number: form.phone_number,
      booth_type: form.booth_type,
      photo_strip_style: form.photo_strip_style || null,
      event_theme_description: form.event_theme_description || null,
      backdrop_id: form.backdrop_id || null,
      selected_addons: form.selected_addons,
      booth_base_rate: boothBaseRate,
      subtotal: subtotal,
      tax: tax,
      quoted_price: total,
    };

    if (user) {
      try {
        await axios.post(`${API}/api/bookings`, bookingData, { withCredentials: true });
        navigate('/dashboard');
      } catch (err) {
        console.error('Booking submission failed:', err);
        setErrors({ submit: err.response?.data?.error || 'Failed to submit booking' });
      }
    } else {
      localStorage.setItem('pixly_pending_booking', JSON.stringify(bookingData));
      navigate('/signup');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0F1F', fontFamily: 'Arial, Helvetica, sans-serif', paddingBottom: 60 }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(227,67,100,0.2)', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <a href="/"><img src={logo} alt="PixlyBooth" style={{ height: 48 }} /></a>
        <span style={{ color: '#a0a0b8', fontSize: 13 }}>Book your booth</span>
      </div>

      {/* Step indicator */}
      <div style={{ maxWidth: 640, margin: '32px auto 0', padding: '0 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 40 }}>
          {STEPS.map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <React.Fragment key={n}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 700,
                    background: done ? '#E34364' : active ? 'rgba(227,67,100,0.15)' : 'rgba(255,255,255,0.05)',
                    color: done || active ? '#fff' : '#555',
                    border: done ? 'none' : active ? '2px solid #E34364' : '2px solid rgba(255,255,255,0.1)',
                  }}>{done ? '✓' : n}</div>
                  <span style={{ fontSize: 11, color: active ? '#fff' : '#555', marginTop: 6, textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 2, background: done ? '#E34364' : 'rgba(255,255,255,0.08)', margin: '0 0 20px' }} />}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && <BookingStep1 form={form} update={update} errors={errors} onNext={handleNext} />}
        {step === 2 && <BookingStep2 form={form} update={update} errors={errors} booths={booths} backdrops={backdrops} dbAddons={dbAddons} onNext={handleNext} onBack={handleBack} />}
        {step === 3 && <BookingStep3 form={form} pricing={pricing} booths={booths} dbAddons={dbAddons} total={calcTotal()} onBack={handleBack} onSubmit={handleSubmit} error={errors.submit} />}
      </div>
    </div>
  );
}
